import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { PhoneSchema } from './auth';
import { AllergenSchema, ItemKindSchema, MenuFoodTypeSchema, SpiceLevelSchema } from './menu';
import { DiscoveryAreaSchema, OutletCardSchema } from './discovery';

/* ------------------------------------------------------------------ */
/* Customer app — browse (W9)                                          */
/*                                                                     */
/* Profile, saved addresses (map pin, landmark, gate photo, voice      */
/* note), area votes where Capsico does not deliver yet, and the       */
/* restaurant page with its customer menu. Cart and checkout are W10.  */
/* ------------------------------------------------------------------ */

export const CUSTOMER_LOCALES = ['en', 'hi', 'ne'] as const;
export const CustomerLocaleSchema = z.enum(CUSTOMER_LOCALES);
export type CustomerLocale = z.infer<typeof CustomerLocaleSchema>;

export const DIETS = ['veg', 'non_veg'] as const;
export const DietSchema = z.enum(DIETS);
export type Diet = z.infer<typeof DietSchema>;

/* ---------------- profile ---------------- */

export const CustomerProfileSchema = z.object({
  id: z.uuid(),
  phone: z.string(),
  country: CountryCodeSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  /** ISO date (YYYY-MM-DD). */
  birthday: z.string().nullable(),
  diet: DietSchema.nullable(),
  locale: CustomerLocaleSchema,
  /** False until the first-time profile step is done (name + diet). */
  complete: z.boolean(),
  version: z.number().int(),
});
export type CustomerProfile = z.infer<typeof CustomerProfileSchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const UpdateProfileInputSchema = z.object({
  name: z.string().trim().min(2, 'Add your name').max(60, 'Keep it under 60 letters'),
  email: z
    .union([z.literal(''), z.email('Enter a valid email').max(120)])
    .nullable()
    .optional()
    .transform((value) => (value === undefined || value === '' ? null : value)),
  birthday: isoDate.nullable().optional(),
  diet: DietSchema,
  locale: CustomerLocaleSchema.optional(),
  /** Omit on the very first save. */
  expectedVersion: z.number().int().min(1).optional(),
});
export type UpdateProfileInput = z.infer<typeof UpdateProfileInputSchema>;

/* ---------------- addresses ---------------- */

export const ADDRESS_LABELS = ['home', 'work', 'other'] as const;
export const AddressLabelSchema = z.enum(ADDRESS_LABELS);
export type AddressLabel = z.infer<typeof AddressLabelSchema>;

export const ADDRESS_MEDIA_KINDS = ['gate_photo', 'voice_note'] as const;
export const AddressMediaKindSchema = z.enum(ADDRESS_MEDIA_KINDS);
export type AddressMediaKind = z.infer<typeof AddressMediaKindSchema>;

export const ADDRESS_MEDIA_TYPES: Record<AddressMediaKind, readonly string[]> = {
  gate_photo: ['image/jpeg', 'image/png', 'image/webp'],
  voice_note: ['audio/webm', 'audio/ogg', 'audio/mp4', 'audio/mpeg', 'audio/aac'],
};
export const ADDRESS_MEDIA_MAX_BYTES: Record<AddressMediaKind, number> = {
  gate_photo: 5 * 1024 * 1024,
  voice_note: 2 * 1024 * 1024,
};
/** Longest voice note a rider has to listen to. */
export const VOICE_NOTE_MAX_SECONDS = 60;

export const MediaUploadInputSchema = z
  .object({
    kind: AddressMediaKindSchema,
    contentType: z.string().trim().min(3).max(60),
    bytes: z.number().int().min(1),
    /** Voice notes only. */
    seconds: z.number().int().min(1).max(VOICE_NOTE_MAX_SECONDS).optional(),
  })
  .superRefine((value, ctx) => {
    if (!ADDRESS_MEDIA_TYPES[value.kind].includes(value.contentType)) {
      ctx.addIssue({
        code: 'custom',
        path: ['contentType'],
        message: 'This file type is not allowed',
      });
    }
    if (value.bytes > ADDRESS_MEDIA_MAX_BYTES[value.kind]) {
      ctx.addIssue({ code: 'custom', path: ['bytes'], message: 'This file is too large' });
    }
    if (value.kind === 'voice_note' && value.seconds === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['seconds'],
        message: 'Say how long the voice note is',
      });
    }
  });
export type MediaUploadInput = z.infer<typeof MediaUploadInputSchema>;

export const MediaUploadTicketSchema = z.object({
  mediaId: z.uuid(),
  url: z.string(),
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string(),
});
export type MediaUploadTicket = z.infer<typeof MediaUploadTicketSchema>;

export const AddressMediaViewSchema = z.object({
  id: z.uuid(),
  kind: AddressMediaKindSchema,
  url: z.string(),
  seconds: z.number().int().nullable(),
});
export type AddressMediaView = z.infer<typeof AddressMediaViewSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === undefined || value === '' ? null : value));

const AddressFieldsSchema = z.object({
  label: AddressLabelSchema,
  /** Only for "other" (e.g. "Hostel", "Mummy's place"). */
  labelText: optionalText(30),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  house: z.string().trim().min(1, 'Add house / flat number').max(80),
  floor: optionalText(30),
  area: z.string().trim().min(3, 'Add the street or area').max(160),
  landmark: optionalText(120),
  instructions: optionalText(200),
  /** Ordering for someone else at this address. */
  receiverName: optionalText(60),
  receiverPhone: PhoneSchema.nullable().optional(),
  gatePhotoId: z.uuid().nullable().optional(),
  voiceNoteId: z.uuid().nullable().optional(),
  makeDefault: z.boolean().optional(),
});

function checkAddress(
  value: { label: AddressLabel; labelText: string | null },
  ctx: z.RefinementCtx,
) {
  if (value.label === 'other' && !value.labelText) {
    ctx.addIssue({ code: 'custom', path: ['labelText'], message: 'Name this place' });
  }
}

export const AddressInputSchema = AddressFieldsSchema.superRefine(checkAddress);
export type AddressInput = z.infer<typeof AddressInputSchema>;

export const UpdateAddressInputSchema = AddressFieldsSchema.extend({
  expectedVersion: z.number().int().min(1),
}).superRefine(checkAddress);
export type UpdateAddressInput = z.infer<typeof UpdateAddressInputSchema>;

export const AddressViewSchema = z.object({
  id: z.uuid(),
  label: AddressLabelSchema,
  labelText: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
  house: z.string(),
  floor: z.string().nullable(),
  area: z.string(),
  landmark: z.string().nullable(),
  instructions: z.string().nullable(),
  receiverName: z.string().nullable(),
  receiverPhone: z.string().nullable(),
  gatePhoto: AddressMediaViewSchema.nullable(),
  voiceNote: AddressMediaViewSchema.nullable(),
  isDefault: z.boolean(),
  /** Whether Capsico delivers here right now (re-checked on every read). */
  serviceable: z.boolean(),
  townName: z.string().nullable(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type AddressView = z.infer<typeof AddressViewSchema>;

/** Most saved addresses per customer. */
export const MAX_ADDRESSES = 20;

/* ---------------- area votes ---------------- */

export const AreaVoteInputSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /** A random id the app keeps per install; only its hash is stored. */
  deviceId: z.string().trim().min(16).max(128),
  /** Optional: text me when Capsico arrives. Signed-in customers' phone is used instead. */
  notifyPhone: PhoneSchema.nullable().optional(),
});
export type AreaVoteInput = z.infer<typeof AreaVoteInputSchema>;

export const AreaVoteResultSchema = z.object({
  /** False when this device (or account) already voted for this area. */
  counted: z.boolean(),
  /** Votes within ~1 km. */
  votesNearby: z.number().int(),
  townName: z.string().nullable(),
});
export type AreaVoteResult = z.infer<typeof AreaVoteResultSchema>;

export const AreaVoteCountQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
});
export type AreaVoteCountQuery = z.infer<typeof AreaVoteCountQuerySchema>;

/* ---------------- restaurant page + customer menu ---------------- */

export const CustomerAddonGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  selection: z.enum(['one', 'many']),
  minSelect: z.number().int(),
  maxSelect: z.number().int(),
  options: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      priceMinor: z.number().int(),
      foodType: MenuFoodTypeSchema,
    }),
  ),
});
export type CustomerAddonGroup = z.infer<typeof CustomerAddonGroupSchema>;

export const CustomerComboSlotSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  pick: z.number().int(),
  options: z.array(z.object({ itemId: z.uuid(), name: z.string(), foodType: MenuFoodTypeSchema })),
});
export type CustomerComboSlot = z.infer<typeof CustomerComboSlotSchema>;

export const CustomerDishSchema = z.object({
  id: z.uuid(),
  kind: ItemKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  foodType: MenuFoodTypeSchema,
  spiceLevel: SpiceLevelSchema.nullable(),
  allergens: z.array(AllergenSchema),
  portion: z.string().nullable(),
  calories: z.number().int().nullable(),
  /** Price before any size choice (the default size, or the base price). */
  priceMinor: z.number().int(),
  variants: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      priceMinor: z.number().int(),
      isDefault: z.boolean(),
    }),
  ),
  addonGroupIds: z.array(z.uuid()),
  comboSlots: z.array(CustomerComboSlotSchema),
  isBestseller: z.boolean(),
  /** W17: customers who liked this dish (90 days); absent when trust is off. */
  likes: z.number().int().optional(),
  photo: z.object({ smallUrl: z.string(), largeUrl: z.string() }).nullable(),
  available: z.boolean(),
  /** "Next available at 6:00 PM" style text when not available now. */
  unavailableText: z.string().nullable(),
  /** Has sizes, add-ons or combo choices — opens the customisation sheet. */
  customisable: z.boolean(),
});
export type CustomerDish = z.infer<typeof CustomerDishSchema>;

export const CustomerMenuSchema = z.object({
  categories: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      description: z.string().nullable(),
      items: z.array(CustomerDishSchema),
    }),
  ),
  addonGroups: z.array(CustomerAddonGroupSchema),
});
export type CustomerMenu = z.infer<typeof CustomerMenuSchema>;

export const OutletPageQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
});
export type OutletPageQuery = z.infer<typeof OutletPageQuerySchema>;

export const RestaurantPageSchema = z.object({
  area: DiscoveryAreaSchema,
  outlet: OutletCardSchema.extend({
    country: CountryCodeSchema,
    address: z.string(),
    coverLargeUrl: z.string().nullable(),
    /** FSSAI (India) / DFTQC (Nepal) licence shown to customers. */
    licence: z.object({ kind: z.enum(['FSSAI', 'DFTQC']), number: z.string() }).nullable(),
    /** False when the outlet is outside the delivery radius for this point right now. */
    deliverable: z.boolean(),
    notDeliverableReason: z.string().nullable(),
  }),
  menu: CustomerMenuSchema,
});
export type RestaurantPage = z.infer<typeof RestaurantPageSchema>;

/* ---------------- helpers shared by the app ---------------- */

/** Total for one configured dish: chosen size (or base) + chosen add-ons, times quantity. */
export function customisedPriceMinor(input: {
  dish: Pick<CustomerDish, 'priceMinor' | 'variants'>;
  variantId: string | null;
  addonOptionPrices: number[];
  quantity: number;
}): number {
  const variant = input.variantId
    ? input.dish.variants.find((entry) => entry.id === input.variantId)
    : undefined;
  const base = variant ? variant.priceMinor : input.dish.priceMinor;
  const addons = input.addonOptionPrices.reduce((sum, price) => sum + price, 0);
  return (base + addons) * Math.max(0, input.quantity);
}
